import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import styles from "properties/propertyPage/_css/propertyUnitsCard.module.css";
import { TUnitListingMdl, UNIT_EVENT } from "units/_models/UnitMdl";
import { observer } from "mobx-react";
import bedroomsIcon from "../../assets/images/bedrooms.svg";
import bathroomsIcon from "../../assets/images/bathrooms.svg";
import { formatPrice } from "_common/_utils/currencyUtils";
import { convert, UNIT_CONVERSION } from "_common/_utils/unityUtils";
import { useOnScreen } from "_common/_utils/useOnScreen";
import { analyticsUtils } from "_common/_utils/analyticsUtils";
import { useLocation } from "react-router";
import { getWebpFile } from "_common/_utils/fileUtils";
import { PROPERTY_PURPOSE } from "properties/_models/PropertyMdl";
import { Tooltip } from "@material-ui/core";

type Props = {
    unit: TUnitListingMdl;
    notDisplayPrice: boolean;
};

export const HorizontalUnitCard = observer(({ unit, notDisplayPrice }: Props) => {
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const url = useLocation().pathname;
    const { isIntersecting } = useOnScreen(ref, 0.5);

    useEffect(() => {
        if (!unit || !unit.localized.title) return;
        if (isIntersecting) {
            void analyticsUtils.tracksEventOfItem(UNIT_EVENT.PRINTED, unit._id, url);
        }
    }, [isIntersecting]);

    return (
        <div ref={ref} className={clsx(styles.card, "flex_row_center mt_15")}>
            <img
                src={getWebpFile(unit.photos[0]?.thumbnail) ?? unit.photos[0]?.url}
                alt={unit.photos[0]?.alt ?? `Photo ${unit.localized.title}`}
                onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = "https://placehold.co/100x100?text=Vistoo.com";
                }}
                className={clsx("flex_column", styles.img)}
            />
            <div className={clsx("flex_row flex-1 p_10 ph_20", styles.contentCard)}>
                <div className={"flex_column justifyContent_spaceAround flex-1"}>
                    <div className={clsx("flex_row_center", styles.firstRow)}>
                        <Tooltip title={unit.localized.title} disableHoverListener={!unit.localized.title || unit.localized.title.length <= 15}>
                            <div className={styles.titleUnit}>{unit.localized.title}</div>
                        </Tooltip>
                        <div className={"flex_row_center"}>
                            <div className={styles.iconWrapper}>
                                <img src={bedroomsIcon} alt={"bedrooms"} className={styles.icon} />
                            </div>
                            <div className={clsx(styles.iconText)}>
                                {unit.bedrooms} {t("propertyPage.unitTypes.bedroom")}
                            </div>
                        </div>
                        <div className={"flex_row_center"}>
                            <div className={styles.iconWrapper}>
                                <img src={bathroomsIcon} alt={"bathrooms"} className={styles.icon} />
                            </div>
                            <div className={styles.iconText}>
                                {unit.bathrooms} {t("propertyPage.unitTypes.bathroom")}
                            </div>
                        </div>
                    </div>
                    {!!unit.squareSurface?.min && (
                        <div className={styles.surfaceText}>
                            {t("unit.squareSurface.surfaceMinOnly", {
                                min: unit.squareSurface?.min,
                            })}
                            {` (${convert(unit.squareSurface.min, UNIT_CONVERSION.FT2_TO_M2)}m²)`}
                        </div>
                    )}
                </div>
                <div className={styles.priceContainer}>
                    {notDisplayPrice ? (
                        <div className={styles.notDisplayedPrice}>{t("propertiesPage.card.priceOnRequest")}</div>
                    ) : (
                        <>
                            <div className={styles.priceLabel}>{t("propertiesPage.card.from")}</div>
                            <div className={styles.price}>
                                {formatPrice(unit.price.min, false)}
                                {unit.property.purpose === PROPERTY_PURPOSE.RENT && t("propertiesPage.card.perMonth")}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
});
